import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CustomIcon } from "../shared/CustomIcon";
import { Avatar } from "../shared/Avatar";
import GivingEntryModal from "./GivingEntryModal";
import ExpenseEntryModal from "./ExpenseEntryModal";
import FinanceDashboard from "./FinanceDashboard";
import FinanceFilterPanel from "./FinanceFilterPanel";
import BulkEntryModal from "./BulkEntryModal";
import FinanceImportModal from "./FinanceImportModal";
import VoidRequestModal from "./VoidRequestModal";
import ActivityLogTab from "./ActivityLogTab";
import { ReceiptGenerateModal, ReceiptBatchModal } from "./ReceiptModals";
import { ReceiptLightbox, receiptFilename } from "./ReceiptLightbox";
import { printGivingReceipt } from "./GivingReceiptPrint";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  apiListGiving, apiListExpenses,
  apiVoidGiving, apiReassignGiving,
  apiVoidApproveGiving, apiVoidRejectGiving,
  apiApproveExpense, apiRejectExpense, apiVoidExpense,
  apiVoidApproveExpense, apiVoidRejectExpense,
  apiPermanentDeleteGiving, apiPermanentDeleteExpense,
  apiAggregate,
} from "./api";
import { apiListDepartments } from "../departments/api";
import { fmtLocalDate, parseLocalDate } from "../../lib/localDate";

// ============================================================
// FINANCE PAGE — private workspace for the Finance department.
// This page also serves 3 alternate views based on access:
//  - Finance team (SA/LA/leader/dept members)  → full workspace
//  - Pastor with detailedGivingAccess=true      → read-only aggregate
//  - Anyone else                                 → restricted-access screen
// ============================================================

// -------------- RESTRICTED ACCESS SCREEN --------------
function FinanceRestrictedScreen({ dept, navigate }) {
  const name = dept?.name || "Finance";
  return (
    <div
      data-testid="finance-restricted"
      style={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        textAlign: "center",
        gap: 18,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 96, height: 96, borderRadius: "50%",
          background: "color-mix(in srgb, var(--kc-accent) 12%, transparent)",
          border: "2px solid color-mix(in srgb, var(--kc-accent) 50%, transparent)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {dept?.logoUrl
          ? <img src={dept.logoUrl} alt="" style={{ width: 64, height: 64, objectFit: "contain" }} />
          : <CustomIcon name="coin" size={56} />}
      </div>
      <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-text)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
        {name}
      </div>
      <div style={{ maxWidth: 340, color: "var(--kc-sub)", fontSize: 15, lineHeight: 1.55 }}>
        This department workspace is restricted to Finance team members only.
      </div>
      <button
        className="btn btn-gold"
        data-testid="finance-restricted-back"
        onClick={() => navigate ? navigate("/departments") : window.history.back()}
        style={{ marginTop: 8 }}
      >
        ← Back
      </button>
    </div>
  );
}

// -------------- PASTOR AGGREGATE VIEW --------------
const PERIODS = ["Day", "Week", "Month", "Year"];

function FinancePastorAggregate({ currencySymbol = "$" }) {
  const [period, setPeriod] = useState("Month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    setLoading(true); setError("");
    apiAggregate(period)
      .then(d => { if (!cancel) setData(d); })
      .catch(e => { if (!cancel) setError(e.message || "Could not load aggregate"); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [period]);

  const chartData = useMemo(
    () => (data?.byCategory || []).map(x => ({
      category: x.category,
      amount: (x.amountCents || 0) / 100,
    })),
    [data]
  );

  const total = data?.totalCents || 0;
  const totalStr = `${currencySymbol}${(total / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div data-testid="finance-pastor-aggregate" style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <CustomIcon name="coin" size={26} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 20, fontWeight: 700, color: "var(--kc-text)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Giving Summary
          </div>
          <div style={{ fontSize: 12, opacity: 0.65 }}>Read-only aggregate — no individual records shown.</div>
        </div>
      </div>

      {/* Period toggle */}
      <div role="tablist" style={{ display: "flex", gap: 4, background: "color-mix(in srgb, var(--kc-text) 6%, transparent)", padding: 4, borderRadius: 10, marginBottom: 16 }}>
        {PERIODS.map(p => (
          <button
            key={p}
            className="btn btn-sm"
            style={{
              flex: 1,
              background: period === p ? "var(--kc-accent)" : "transparent",
              color: period === p ? "#111" : "var(--kc-text)",
              fontWeight: 700,
              minHeight: 36,
            }}
            onClick={() => setPeriod(p)}
            data-testid={`finance-pastor-period-${p}`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Total card */}
      <div
        style={{
          padding: 20,
          background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)",
          border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)",
          borderRadius: 12,
          textAlign: "center",
          marginBottom: 20,
        }}
        data-testid="finance-pastor-total"
      >
        <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1.4 }}>
          Total giving received — {period}
        </div>
        <div style={{ fontSize: 32, fontWeight: 700, color: "var(--kc-accent)", marginTop: 4 }}>{totalStr}</div>
        <div style={{ fontSize: 12, opacity: 0.65, marginTop: 2 }}>{data?.recordCount || 0} records</div>
      </div>

      {loading && <div style={{ opacity: 0.6, padding: 20, textAlign: "center" }} data-testid="finance-pastor-loading">Loading…</div>}
      {error && <div className="alert-error" data-testid="finance-pastor-error" style={{ padding: 12, background: "rgba(200,50,50,0.15)", color: "#f2a2a2", borderRadius: 8 }}>{error}</div>}

      {!loading && !error && chartData.length === 0 && (
        <div style={{ opacity: 0.6, padding: 20, textAlign: "center" }} data-testid="finance-pastor-empty">
          No giving records in this period yet.
        </div>
      )}

      {!loading && !error && chartData.length > 0 && (
        <div
          style={{ background: "color-mix(in srgb, var(--kc-text) 3%, transparent)", padding: 12, borderRadius: 12, border: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)" }}
          data-testid="finance-pastor-chart"
        >
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8, letterSpacing: 1, textTransform: "uppercase" }}>By Category</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="color-mix(in srgb, var(--kc-text) 10%, transparent)" />
              <XAxis
                dataKey="category"
                tick={{ fill: "var(--kc-sub)", fontSize: 11 }}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={50}
              />
              <YAxis
                tick={{ fill: "var(--kc-sub)", fontSize: 11 }}
                tickFormatter={(v) => `${currencySymbol}${v.toLocaleString()}`}
                width={70}
              />
              <Tooltip
                cursor={{ fill: "color-mix(in srgb, var(--kc-accent) 8%, transparent)" }}
                contentStyle={{ background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: 8, color: "var(--kc-text)" }}
                formatter={(v) => [`${currencySymbol}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, "Amount"]}
              />
              <Bar dataKey="amount" fill="var(--kc-accent)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "giving",    label: "Giving Records" },
  { key: "expenses",  label: "Expenses" },
  { key: "pending",   label: "Pending Approvals" },
  { key: "voided",    label: "Voided" },
  { key: "anonymous", label: "Anonymous" },
  { key: "activity",  label: "Activity Log" },
];

// Category + method vocab used by both the entry modals and the filter panel.
// Kept in-sync with GivingEntryModal.jsx / ExpenseEntryModal.jsx.
const GIVING_CATEGORIES  = ["Regular Offering", "Tithe", "Seed", "Alms", "Other"];
const EXPENSE_CATEGORIES = [
  "Mortgage/Rent", "Staff Wages", "Guest Speakers", "Equipment/Materials",
  "Pastor Stipend", "Utilities", "Legal Fees", "Bank Fees", "Benevolence",
  "Missionary Support", "Staff Travel", "Ministry Education", "Licenses",
  "Insurances", "Payroll Taxes", "Other",
];
const PAYMENT_METHODS    = ["Cash", "Cheque", "E-Transfer", "Card"];
const EXPENSE_STATUSES   = ["Pending", "Approved", "Rejected"];

const EMPTY_GIVING_FILTERS = {
  dateRange:      { from: "", to: "" },
  memberQuery:    "",
  categories:     [],
  paymentMethods: [],
  anonymousOnly:  false,
  amountRange:    { min: "", max: "" },
};
const EMPTY_EXPENSE_FILTERS = {
  dateRange:   { from: "", to: "" },
  payeeQuery:  "",
  categories:  [],
  statuses:    [],
  amountRange: { min: "", max: "" },
};

// Sort configs for the sortable Giving Records table.
const GIVING_SORT_KEYS = {
  date:     (r, memberMap) => r.date || "",
  member:   (r, memberMap) => r.isAnonymous ? "~~anonymous" : (fullName(memberMap[r.memberId]) || "").toLowerCase(),
  category: (r) => (r.category === "Other" ? (r.categoryOther || "Other") : r.category || "").toLowerCase(),
  amount:   (r) => Number(r.amountCents) || 0,
  ref:      (r) => (r.transactionRef || "").toLowerCase(),
};

function fmt(cents, sym = "$") {
  if (!Number.isFinite(cents)) return `${sym}0.00`;
  return `${sym}${(cents / 100).toFixed(2)}`;
}

function fmtDate(iso) {
  return fmtLocalDate(iso);
}

function fullName(m) {
  if (!m) return "—";
  return `${m.firstName || ""} ${m.lastName || ""}`.trim() || "—";
}

// Iter 84 — resolve the CURRENT memberNumber from the live member record.
// Never rely on a cached copy on the giving/expense row — numbers can be
// renumbered after a record was created.
function memberNumberOf(memberMap, memberId) {
  if (!memberId) return "";
  const m = memberMap[memberId];
  return (m && m.memberNumber) ? String(m.memberNumber) : "";
}

// Iter 84 — Period-selector helpers (Daily / Weekly / Monthly / Yearly / Lifetime).
const TOTAL_PERIODS = ["Daily", "Weekly", "Monthly", "Yearly", "Lifetime"];

/** Return true when a YYYY-MM-DD `iso` falls in the given period *ending today*. */
function isoInPeriod(iso, period) {
  if (period === "Lifetime") return true;
  if (!iso) return false;
  const d = parseLocalDate(iso);
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (period === "Daily") {
    return d.getFullYear() === today.getFullYear()
        && d.getMonth() === today.getMonth()
        && d.getDate() === today.getDate();
  }
  if (period === "Weekly") {
    const start = new Date(today);
    start.setDate(start.getDate() - 6);
    return d >= start && d <= today;
  }
  if (period === "Monthly") {
    return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth();
  }
  if (period === "Yearly") {
    return d.getFullYear() === today.getFullYear();
  }
  return false;
}

/** Group records by year → month (both keyed most-recent first). */
function groupByYearMonth(records) {
  const buckets = {};
  for (const r of records) {
    if (!r?.date) continue;
    const parts = String(r.date).slice(0, 7).split("-"); // "YYYY-MM"
    if (parts.length !== 2) continue;
    const [y, m] = parts;
    if (!buckets[y]) buckets[y] = {};
    if (!buckets[y][m]) buckets[y][m] = [];
    buckets[y][m].push(r);
  }
  const years = Object.keys(buckets).sort((a, b) => Number(b) - Number(a));
  return years.map(year => {
    const months = Object.keys(buckets[year]).sort((a, b) => Number(b) - Number(a));
    return {
      year,
      yearTotalCents: months.reduce((s, m) => s + buckets[year][m].reduce((sm, r) => sm + (Number(r.amountCents) || 0), 0), 0),
      months: months.map(month => ({
        month,          // "01".."12"
        monthKey: `${year}-${month}`,
        monthLabel: new Date(Number(year), Number(month) - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" }),
        monthTotalCents: buckets[year][month].reduce((s, r) => s + (Number(r.amountCents) || 0), 0),
        // Sort records within a month by date desc.
        records: [...buckets[year][month]].sort((a, b) => (b.date || "").localeCompare(a.date || "")),
      })),
    };
  });
}

// ---- prompt helper (falls back to window.prompt when we don't need a custom modal) ----
async function askReason(title, defaultVal = "") {
  // Use built-in prompt — simple and thumb-friendly enough for the reason input.
  const v = window.prompt(title, defaultVal);
  return v === null ? null : v.trim();
}


export function FinancePage({ currentUser, members = [], appSettings = {}, navigate }) {
  const currencySymbol = appSettings.currencySymbol || "$";
  const thresholds = {
    leader: Number(appSettings.expenseDeptLeaderThresholdCents) || 10000,
    admin: Number(appSettings.expenseAdminThresholdCents) || 50000,
  };
  const role = currentUser?.role;

  // ---- Load Finance dept for access gating (leader + members) ----
  const [dept, setDept] = useState(null);
  const [deptLoaded, setDeptLoaded] = useState(false);
  useEffect(() => {
    let cancel = false;
    apiListDepartments()
      .then(list => {
        if (cancel) return;
        const fin = (Array.isArray(list) ? list : []).find(d => d.isFinance || d.id === "dept_finance");
        setDept(fin || null);
      })
      .catch(() => {})
      .finally(() => { if (!cancel) setDeptLoaded(true); });
    return () => { cancel = true; };
  }, []);

  const isAdmin = role === "superadmin" || role === "limited_admin";
  const isSuperAdmin = role === "superadmin";
  const isFinanceMember = Boolean(dept && (dept.users || []).includes(currentUser?.id));
  const isFinanceLeader = Boolean(dept && dept.leaderUserId === currentUser?.id);
  const canAccessFinance = isAdmin || isFinanceMember || isFinanceLeader;
  const isFinanceAdmin = isAdmin || isFinanceLeader;
  // Iter-97 — Finance-team member edit permission (parity with backend
  // `require_finance_write`). Non-admin edit members must be able to
  // submit Void Requests, so the Void button visibility uses `canVoid`
  // below (SA / LA / Finance Leader / edit team member).
  const financeMemberPerm = (() => {
    if (!dept || !isFinanceMember) return null;
    const raw = (dept.userPermissions || {})[currentUser?.id];
    const p = raw && typeof raw === "object" ? raw.permission : raw;
    return p === "view" ? "view" : "edit";
  })();
  const canFinanceWrite = isSuperAdmin || isFinanceLeader || financeMemberPerm === "edit";
  const canVoid = canFinanceWrite || role === "limited_admin";
  const isPastorDGA = role === "pastor" && Boolean(currentUser?.detailedGivingAccess);
  const navHook = useNavigate();

  const [tab, setTab] = useState("dashboard");
  const [giving, setGiving] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLauncher, setShowLauncher] = useState(false);
  const [showGivingModal, setShowGivingModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showBulkGivingModal, setShowBulkGivingModal] = useState(false);
  const [importKind, setImportKind] = useState(null);   // "giving" | "expense" | null
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showBatchReceiptModal, setShowBatchReceiptModal] = useState(false);
  // Iter-95 Item 7 — lightbox for expense receipts.
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxFilename, setLightboxFilename] = useState("");
  const openReceipt = (src, filename) => { setLightboxSrc(src); setLightboxFilename(filename || "receipt"); };
  const [editGiving, setEditGiving] = useState(null);
  const [editExpense, setEditExpense] = useState(null);
  const [reassignRec, setReassignRec] = useState(null);
  const [reassignSearch, setReassignSearch] = useState("");
  // Giving-records table: current sort key + direction
  const [givingSort, setGivingSort] = useState({ key: "date", dir: "desc" });
  // Filter panel state (open + values) — one set per data type
  const [givingFilterOpen, setGivingFilterOpen] = useState(false);
  const [givingFilters, setGivingFilters] = useState(EMPTY_GIVING_FILTERS);
  const [expenseFilterOpen, setExpenseFilterOpen] = useState(false);
  const [expenseFilters, setExpenseFilters] = useState(EMPTY_EXPENSE_FILTERS);
  // Iter 84 — period selector for the summary total (Daily/Weekly/Monthly/Yearly/Lifetime).
  const [givingTotalPeriod, setGivingTotalPeriod] = useState("Monthly");
  const [expenseTotalPeriod, setExpenseTotalPeriod] = useState("Monthly");
  // Iter 84 — expand/collapse state for year + month groups (per tab).
  const currentYearStr = String(new Date().getFullYear());
  const currentMonthStr = String(new Date().getMonth() + 1).padStart(2, "0");
  const currentMonthKey = `${currentYearStr}-${currentMonthStr}`;
  const [givingExpandedYears, setGivingExpandedYears]   = useState({ [currentYearStr]: true });
  const [givingExpandedMonths, setGivingExpandedMonths] = useState({ [currentMonthKey]: true });
  const [expenseExpandedYears, setExpenseExpandedYears]   = useState({ [currentYearStr]: true });
  const [expenseExpandedMonths, setExpenseExpandedMonths] = useState({ [currentMonthKey]: true });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [g, e] = await Promise.all([
        apiListGiving().catch(() => []),
        apiListExpenses().catch(() => []),
      ]);
      setGiving(Array.isArray(g) ? g : []);
      setExpenses(Array.isArray(e) ? e : []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const memberMap = useMemo(() => {
    const m = {};
    (members || []).forEach(x => { if (x && x.id) m[x.id] = x; });
    return m;
  }, [members]);

  // ---- Filtered per-tab lists ----
  const activeGiving = useMemo(() => giving.filter(r => !r.voidedAt), [giving]);
  const activeExpenses = useMemo(() => expenses.filter(r => !r.voidedAt), [expenses]);
  const voidedRecords = useMemo(() => {
    const gv = giving.filter(r => r.voidedAt).map(r => ({ ...r, __kind: "giving" }));
    const ev = expenses.filter(r => r.voidedAt).map(r => ({ ...r, __kind: "expense" }));
    return [...gv, ...ev].sort((a, b) => (b.voidedAt || "").localeCompare(a.voidedAt || ""));
  }, [giving, expenses]);
  const pendingExpenses = useMemo(
    () => activeExpenses.filter(r => r.approvalStatus === "Pending"),
    [activeExpenses]
  );
  // Iter-96 Item 5 — void requests waiting on SA/LA approval.
  const pendingGivingVoidReqs  = useMemo(
    () => giving.filter(r => r.voidRequestedAt && !r.voidedAt),
    [giving]
  );
  const pendingExpenseVoidReqs = useMemo(
    () => expenses.filter(r => r.voidRequestedAt && !r.voidedAt),
    [expenses]
  );
  const totalPendingApprovalsCount =
    pendingExpenses.length + pendingGivingVoidReqs.length + pendingExpenseVoidReqs.length;
  const anonymousGiving = useMemo(
    () => activeGiving.filter(r => r.isAnonymous),
    [activeGiving]
  );

  // ---- Filter matchers -----------------------------------------
  // Both match* helpers accept the full filter object; each returns
  // true if the record passes every populated filter.
  const filteredActiveGiving = useMemo(
    () => activeGiving.filter(r => matchGivingFilters(r, givingFilters, memberMap)),
    [activeGiving, givingFilters, memberMap]
  );
  const filteredActiveExpenses = useMemo(
    () => activeExpenses.filter(r => matchExpenseFilters(r, expenseFilters)),
    [activeExpenses, expenseFilters]
  );

  // Active filter chips per tab — array of `{ id, label, onRemove }`.
  const givingChips  = useMemo(() => buildGivingChips(givingFilters, setGivingFilters, currencySymbol),  [givingFilters, currencySymbol]);
  const expenseChips = useMemo(() => buildExpenseChips(expenseFilters, setExpenseFilters, currencySymbol), [expenseFilters, currencySymbol]);

  // Sorted view of the Giving Records tab based on the header the user clicked.
  const sortedGiving = useMemo(() => {
    const accessor = GIVING_SORT_KEYS[givingSort.key] || GIVING_SORT_KEYS.date;
    const dir = givingSort.dir === "asc" ? 1 : -1;
    return [...filteredActiveGiving].sort((a, b) => {
      const av = accessor(a, memberMap);
      const bv = accessor(b, memberMap);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filteredActiveGiving, givingSort, memberMap]);

  const toggleGivingSort = (key) => {
    setGivingSort(cur => {
      if (cur.key === key) return { key, dir: cur.dir === "asc" ? "desc" : "asc" };
      // Default direction: dates/amounts desc; text asc.
      const desc = key === "date" || key === "amount";
      return { key, dir: desc ? "desc" : "asc" };
    });
  };

  // ---- Actions ----
  // Iter-96 Item 5 — Void requests
  // SA/LA voiding directly is still supported (backend routes them as
  // "Void Approved" in one step). Finance Leader / Team Member trigger a
  // pending void request that admins can Approve or Reject.
  const [voidReqTarget, setVoidReqTarget] = useState(null); // { rec, kind }
  const [voidRejectTarget, setVoidRejectTarget] = useState(null); // { rec, kind }

  const openVoidRequest = (rec, kind) => setVoidReqTarget({ rec, kind });
  const submitVoidRequest = async (reason) => {
    if (!voidReqTarget) return;
    const { rec, kind } = voidReqTarget;
    try {
      if (kind === "giving") await apiVoidGiving(rec.recordId, reason);
      else await apiVoidExpense(rec.recordId, reason);
      setVoidReqTarget(null);
      await reload();
    } catch (e) {
      alert(e.message || "Void request failed");
      throw e;
    }
  };
  const approveVoidRequest = async (rec, kind) => {
    try {
      if (kind === "giving") await apiVoidApproveGiving(rec.recordId);
      else await apiVoidApproveExpense(rec.recordId);
      await reload();
    } catch (e) { alert(e.message || "Approve void failed"); }
  };
  const openRejectVoid = (rec, kind) => setVoidRejectTarget({ rec, kind });
  const submitVoidRejection = async (reason) => {
    if (!voidRejectTarget) return;
    const { rec, kind } = voidRejectTarget;
    try {
      if (kind === "giving") await apiVoidRejectGiving(rec.recordId, reason);
      else await apiVoidRejectExpense(rec.recordId, reason);
      setVoidRejectTarget(null);
      await reload();
    } catch (e) {
      alert(e.message || "Reject void failed");
      throw e;
    }
  };

  const onVoidGiving = (rec) => openVoidRequest(rec, "giving");
  const onVoidExpense = (rec) => openVoidRequest(rec, "expense");
  // Iter 84 — permanent delete for voided records (Super Admin only).
  const [deleteTarget, setDeleteTarget] = useState(null); // { rec, kind: "giving"|"expense" }
  const commitPermanentDelete = async () => {
    if (!deleteTarget) return;
    const { rec, kind } = deleteTarget;
    try {
      if (kind === "giving") await apiPermanentDeleteGiving(rec.recordId);
      else await apiPermanentDeleteExpense(rec.recordId);
      setDeleteTarget(null);
      await reload();
    } catch (e) { alert(e.message || "Permanent delete failed"); }
  };
  const onReassign = (rec) => { setReassignRec(rec); setReassignSearch(""); };
  const commitReassign = async (memberId) => {
    if (!reassignRec) return;
    try { await apiReassignGiving(reassignRec.recordId, memberId); setReassignRec(null); await reload(); }
    catch (e) { alert(e.message || "Reassign failed"); }
  };
  const onApprove = async (rec) => {
    try { await apiApproveExpense(rec.recordId); await reload(); }
    catch (e) { alert(e.message || "Approve failed"); }
  };
  const onReject = async (rec) => {
    const reason = await askReason(`Reject expense for ${rec.payeeName}?\nEnter a rejection reason:`);
    if (!reason) return;
    try { await apiRejectExpense(rec.recordId, reason); await reload(); }
    catch (e) { alert(e.message || "Reject failed"); }
  };

  const openLogGiving = () => { setEditGiving(null); setShowGivingModal(true); setShowLauncher(false); };
  const openBulkGiving = () => { setShowBulkGivingModal(true); setShowLauncher(false); };
  const openLogExpense = () => { setEditExpense(null); setShowExpenseModal(true); setShowLauncher(false); };

  // -------- Toolbar (Filter + chips + Export CSV) --------
  const renderTableToolbar = ({ onFilter, activeCount, chips, onExport, exportDisabled, testidPrefix, extraLeftButtons = null }) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button
          className="btn btn-outline btn-sm"
          onClick={onFilter}
          data-testid={`${testidPrefix}-filter-btn`}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <CustomIcon name="settings" size={12} /> Filter
          {activeCount > 0 && (
            <span
              data-testid={`${testidPrefix}-filter-badge`}
              style={{ marginLeft: 2, padding: "1px 7px", background: "var(--kc-accent)", color: "#111", borderRadius: 999, fontSize: 11, fontWeight: 800 }}
            >
              {activeCount}
            </span>
          )}
        </button>
        {extraLeftButtons}
        <div style={{ flex: 1 }} />
        <button
          className="btn btn-outline btn-sm"
          onClick={onExport}
          disabled={exportDisabled}
          data-testid={`${testidPrefix}-export-csv`}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, opacity: exportDisabled ? 0.5 : 1 }}
          title="Export currently filtered and sorted records as CSV"
        >
          <CustomIcon name="document" size={12} /> Export CSV
        </button>
      </div>
      {chips.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }} data-testid={`${testidPrefix}-chips`}>
          {chips.map(chip => (
            <button
              key={chip.id}
              type="button"
              onClick={chip.onRemove}
              data-testid={`${testidPrefix}-chip-${chip.id}`}
              title="Remove filter"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "4px 10px",
                borderRadius: 999,
                background: "color-mix(in srgb, var(--kc-accent) 18%, transparent)",
                border: "1px solid var(--kc-accent)",
                color: "var(--kc-accent)",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <span>{chip.label}</span>
              <span aria-hidden="true" style={{ fontWeight: 800, lineHeight: 1 }}>×</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // -------- Sortable Giving Records TABLE --------
  const sortIndicator = (key) => {
    if (givingSort.key !== key) return <span style={{ opacity: 0.35, marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4, color: "var(--kc-accent)" }}>{givingSort.dir === "asc" ? "▲" : "▼"}</span>;
  };

  const renderGivingTable = () => (
    <div style={{ overflowX: "auto", border: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)", borderRadius: 10 }} data-testid="finance-giving-table-wrap">
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="finance-giving-table">
        <thead>
          <tr style={{ background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", color: "var(--kc-accent)" }}>
            <SortableTh label="Date"     testid="finance-giving-th-date"     onClick={() => toggleGivingSort("date")}     indicator={sortIndicator("date")} />
            <SortableTh label="Member"   testid="finance-giving-th-member"   onClick={() => toggleGivingSort("member")}   indicator={sortIndicator("member")} />
            <SortableTh label="Category" testid="finance-giving-th-category" onClick={() => toggleGivingSort("category")} indicator={sortIndicator("category")} />
            <SortableTh label="Amount"   align="right" testid="finance-giving-th-amount" onClick={() => toggleGivingSort("amount")} indicator={sortIndicator("amount")} />
            <SortableTh label="Ref"      testid="finance-giving-th-ref"      onClick={() => toggleGivingSort("ref")}      indicator={sortIndicator("ref")} />
            <th style={{ ...GIV_TH, textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedGiving.map(r => {
            const num = memberNumberOf(memberMap, r.memberId);
            return (
            <tr key={r.recordId} data-testid={`finance-giving-trow-${r.recordId}`}
                className="card-green"
                style={{
                  borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 10%, transparent)",
                  boxShadow: "inset 3px 0 0 0 var(--kc-green)",
                }}>
              <td style={GIV_TD}>{fmtDate(r.date)}</td>
              <td style={GIV_TD}>
                {r.isAnonymous
                  ? <span style={{ opacity: 0.75, fontStyle: "italic" }}>Anonymous</span>
                  : (
                    <>
                      {fullName(memberMap[r.memberId])}
                      {num && <span data-testid={`finance-giving-membernum-${r.recordId}`} style={{ marginLeft: 6, fontSize: 11, color: "var(--kc-meta)", fontFamily: "monospace" }}>#{num}</span>}
                    </>
                  )}
              </td>
              <td style={GIV_TD}>{r.category === "Other" ? (r.categoryOther || "Other") : r.category}</td>
              <td style={{ ...GIV_TD, textAlign: "right", color: "var(--kc-accent)", fontWeight: 700, fontFamily: "monospace" }}>{fmt(r.amountCents, currencySymbol)}</td>
              <td style={{ ...GIV_TD, fontFamily: "monospace", fontSize: 12, opacity: 0.85 }}>{r.transactionRef}</td>
              <td style={{ ...GIV_TD, textAlign: "right" }}>
                <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {!r.isAnonymous && (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => printGivingReceipt({ record: r, member: memberMap[r.memberId], appSettings, currencySymbol })}
                      data-testid={`finance-giving-print-${r.recordId}`}
                      title="Print Receipt"
                    >
                      <CustomIcon name="document" size={12} style={{ marginRight: 4 }} /> Print Receipt
                    </button>
                  )}
                  <button className="btn btn-outline btn-sm" onClick={() => { setEditGiving(r); setShowGivingModal(true); }} data-testid={`finance-giving-edit-${r.recordId}`}>Edit</button>
                  {isFinanceAdmin && r.isAnonymous && (
                    <button className="btn btn-outline btn-sm" onClick={() => onReassign(r)} data-testid={`finance-giving-reassign-${r.recordId}`}>Reassign</button>
                  )}
                  {canVoid && (
                    <button className="btn btn-outline btn-sm" style={{ color: "#f2a2a2", borderColor: "#f2a2a2" }} onClick={() => onVoidGiving(r)} data-testid={`finance-giving-void-${r.recordId}`}>Void</button>
                  )}
                </div>
              </td>
            </tr>
          );})}
        </tbody>
      </table>
    </div>
  );

  // Iter 84 — Year → Month collapsible groups. Used inside the Giving Records
  // and Expenses tabs. Records within each month use the compact row renderer
  // shared with the flat table (so member number + actions stay in sync).
  const renderGivingRow = (r) => {
    const num = memberNumberOf(memberMap, r.memberId);
    return (
      <div key={r.recordId} data-testid={`finance-giving-row-${r.recordId}`}
           className="finance-row card-green"
           style={{
             padding: 12, marginBottom: 8,
             background: "var(--kc-surface)",
             borderRadius: 10,
             border: "1px solid var(--kc-border)",
             borderLeft: "3px solid var(--kc-green)",
             display: "flex", flexDirection: "column", gap: 6,
           }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 700, color: "var(--kc-text)", fontSize: 14 }}>
              {r.isAnonymous
                ? <span style={{ fontStyle: "italic", opacity: 0.8 }}>Anonymous</span>
                : fullName(memberMap[r.memberId])}
              {num && (
                <span data-testid={`finance-giving-membernum-${r.recordId}`} style={{ marginLeft: 8, fontSize: 11, color: "var(--kc-meta)", fontFamily: "monospace" }}>#{num}</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "var(--kc-sub)", marginTop: 2 }}>
              {fmtDate(r.date)} · {r.category === "Other" ? (r.categoryOther || "Other") : r.category}
              {r.transactionRef && <> · <span style={{ fontFamily: "monospace", opacity: 0.85 }}>{r.transactionRef}</span></>}
            </div>
          </div>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 18, fontWeight: 700, color: "var(--kc-accent)" }}>
            {fmt(r.amountCents, currencySymbol)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} onClick={e => e.stopPropagation()}>
          {!r.isAnonymous && (
            <button className="btn btn-outline btn-sm" onClick={() => printGivingReceipt({ record: r, member: memberMap[r.memberId], appSettings, currencySymbol })} data-testid={`finance-giving-print-${r.recordId}`}>
              <CustomIcon name="document" size={12} style={{ marginRight: 4 }} /> Receipt
            </button>
          )}
          <button className="btn btn-outline btn-sm" onClick={() => { setEditGiving(r); setShowGivingModal(true); }} data-testid={`finance-giving-edit-${r.recordId}`}>Edit</button>
          {isFinanceAdmin && r.isAnonymous && (
            <button className="btn btn-outline btn-sm" onClick={() => onReassign(r)} data-testid={`finance-giving-reassign-${r.recordId}`}>Reassign</button>
          )}
          {canVoid && (
            <button className="btn btn-outline btn-sm" style={{ color: "#f2a2a2", borderColor: "#f2a2a2" }} onClick={() => onVoidGiving(r)} data-testid={`finance-giving-void-${r.recordId}`}>Void</button>
          )}
        </div>
      </div>
    );
  };

  const renderGrouped = ({ records, expandedYears, setExpandedYears, expandedMonths, setExpandedMonths, rowRenderer, testidPrefix }) => {
    const groups = groupByYearMonth(records);
    if (groups.length === 0) return null;
    const toggleYear  = (y) => setExpandedYears(prev => ({ ...prev, [y]: !prev[y] }));
    const toggleMonth = (mk) => setExpandedMonths(prev => ({ ...prev, [mk]: !prev[mk] }));
    return (
      <div data-testid={`${testidPrefix}-grouped`}>
        {groups.map(({ year, yearTotalCents, months }) => {
          const yOpen = expandedYears[year] !== false; // undefined defaults to true when preset; explicit false collapses
          const isCurrentYear = year === currentYearStr;
          const yearOpenResolved = year in expandedYears ? expandedYears[year] : isCurrentYear;
          return (
            <div key={year} data-testid={`${testidPrefix}-year-${year}`} style={{ marginBottom: 10, border: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)", borderRadius: 10, overflow: "hidden" }}>
              <button
                type="button"
                onClick={() => toggleYear(year)}
                data-testid={`${testidPrefix}-year-toggle-${year}`}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 14px",
                  background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)",
                  border: "none", cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span style={{ transition: "transform 0.15s", transform: yearOpenResolved ? "rotate(90deg)" : "rotate(0deg)", color: "var(--kc-accent)", fontWeight: 700 }}>▶</span>
                <span style={{ fontFamily: "'Cinzel',serif", fontSize: 18, fontWeight: 700, color: "var(--kc-text)", letterSpacing: "0.04em" }}>{year}</span>
                <span style={{ flex: 1 }} />
                <span style={{ fontFamily: "'Cinzel',serif", fontSize: 16, fontWeight: 700, color: "var(--kc-accent)" }}>{fmt(yearTotalCents, currencySymbol)}</span>
              </button>
              {yearOpenResolved && (
                <div style={{ padding: "6px 10px 10px" }}>
                  {months.map(({ month, monthKey, monthLabel, monthTotalCents, records: rr }) => {
                    const isCurrentMonth = monthKey === currentMonthKey;
                    const mOpenResolved = monthKey in expandedMonths ? expandedMonths[monthKey] : isCurrentMonth;
                    return (
                      <div key={monthKey} data-testid={`${testidPrefix}-month-${monthKey}`} style={{ marginTop: 6, border: "1px solid color-mix(in srgb, var(--kc-accent) 10%, transparent)", borderRadius: 8, overflow: "hidden" }}>
                        <button
                          type="button"
                          onClick={() => toggleMonth(monthKey)}
                          data-testid={`${testidPrefix}-month-toggle-${monthKey}`}
                          style={{
                            width: "100%", display: "flex", alignItems: "center", gap: 10,
                            padding: "8px 12px",
                            background: "color-mix(in srgb, var(--kc-accent) 5%, transparent)",
                            border: "none", cursor: "pointer",
                            textAlign: "left",
                          }}
                        >
                          <span style={{ transition: "transform 0.15s", transform: mOpenResolved ? "rotate(90deg)" : "rotate(0deg)", color: "var(--kc-sub)", fontSize: 12 }}>▶</span>
                          <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700, color: "var(--kc-text)" }}>{monthLabel}</span>
                          <span style={{ flex: 1 }} />
                          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--kc-accent)", fontFamily: "monospace" }}>{fmt(monthTotalCents, currencySymbol)}</span>
                        </button>
                        {mOpenResolved && (
                          <div style={{ padding: "8px 10px" }}>
                            {rr.map(rowRenderer)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Period-total summary card + segmented control (Fix 7).
  const renderPeriodSummary = ({ period, setPeriod, records, testidPrefix, label }) => {
    const total = records
      .filter(r => isoInPeriod(r.date, period))
      .reduce((s, r) => s + (Number(r.amountCents) || 0), 0);
    return (
      <div data-testid={`${testidPrefix}-total-summary`} style={{ marginBottom: 12, padding: 12, borderRadius: 10, background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 22%, transparent)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--kc-sub)", fontWeight: 700 }}>{label} · {period}</div>
            <div data-testid={`${testidPrefix}-total-amount`} style={{ fontFamily: "'Cinzel',serif", fontSize: 26, fontWeight: 700, color: "var(--kc-accent)", lineHeight: 1.1 }}>
              {fmt(total, currencySymbol)}
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, background: "color-mix(in srgb, var(--kc-text) 6%, transparent)", padding: 4, borderRadius: 999 }} role="tablist" data-testid={`${testidPrefix}-period-selector`}>
            {TOTAL_PERIODS.map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                data-testid={`${testidPrefix}-period-${p}`}
                aria-pressed={period === p}
                style={{
                  padding: "6px 12px",
                  borderRadius: 999,
                  border: "none",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  background: period === p ? "var(--kc-accent)" : "transparent",
                  color: period === p ? "#111" : "var(--kc-text)",
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderExpenseRow = (r, includeApprovalButtons = false) => {
    const statusColor = r.approvalStatus === "Approved" ? "#4ade80" : r.approvalStatus === "Rejected" ? "#f87171" : "var(--kc-accent)";
    // Semantic left border (Issue 4): approved=green, pending=amber, rejected=red
    const variant = r.approvalStatus === "Approved" ? "green"
      : r.approvalStatus === "Rejected" ? "red"
      : "amber";
    const borderColor = variant === "green" ? "var(--kc-green)"
      : variant === "red" ? "var(--kc-red)"
      : "var(--kc-amber)";
    return (
      <div key={r.recordId} className={`finance-row card-${variant}`} data-testid={`finance-expense-row-${r.recordId}`}
           style={{ padding: 12, marginBottom: 8, background: "var(--kc-surface)", borderRadius: 10, border: "1px solid var(--kc-border)", borderLeft: `3px solid ${borderColor}` }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          {/* Iter-95 Item 7 — receipt shows as a text link, not a circular
              profile-style thumbnail. Clicking opens the ReceiptLightbox
              overlay, not a new browser tab. */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{r.payeeName} — {fmt(r.amountCents, currencySymbol)}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              {r.category === "Other" ? r.categoryOther : r.category} · {fmtDate(r.date)} · <span style={{ color: statusColor, fontWeight: 600 }}>{r.approvalStatus}</span>
            </div>
            {r.receiptImage && (
              <button
                type="button"
                data-testid={`finance-expense-receipt-link-${r.recordId}`}
                onClick={(e) => { e.stopPropagation(); openReceipt(r.receiptImage, receiptFilename(r.receiptImage, r.recordId)); }}
                style={{
                  marginTop: 4,
                  background: "transparent", border: "none", padding: 0,
                  color: "var(--kc-accent)",
                  fontSize: 12, cursor: "pointer",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                title={receiptFilename(r.receiptImage, r.recordId, 200)}
              >
                <CustomIcon name="paperclip" size={11} style={{ marginRight: 4 }} />
                {receiptFilename(r.receiptImage, r.recordId)}
              </button>
            )}
            {r.approvalStatus === "Rejected" && r.rejectedReason && (
              <div style={{ fontSize: 12, color: "#f87171", marginTop: 2 }}>Reason: {r.rejectedReason}</div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {includeApprovalButtons && r.approvalStatus === "Pending" && (
            <>
              <button
                className="btn"
                style={{ background: "#4ade80", color: "#111", fontWeight: 800, minHeight: 48, padding: "0 22px", fontSize: 15, borderRadius: 10, flex: "1 1 140px" }}
                onClick={() => onApprove(r)}
                data-testid={`finance-expense-approve-${r.recordId}`}
              >
                <CustomIcon name="check" size={16} style={{ marginRight: 6 }} /> Approve
              </button>
              <button
                className="btn"
                style={{ background: "#f87171", color: "#111", fontWeight: 800, minHeight: 48, padding: "0 22px", fontSize: 15, borderRadius: 10, flex: "1 1 140px" }}
                onClick={() => onReject(r)}
                data-testid={`finance-expense-reject-${r.recordId}`}
              >
                <CustomIcon name="warning" size={16} style={{ marginRight: 6 }} /> Reject
              </button>
            </>
          )}
          {r.receiptImage && (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => openReceipt(r.receiptImage, receiptFilename(r.receiptImage, r.recordId))}
              data-testid={`finance-expense-viewreceipt-${r.recordId}`}
            >View Receipt</button>
          )}
          {r.approvalStatus === "Pending" && (
            <button className="btn btn-outline btn-sm" onClick={() => { setEditExpense(r); setShowExpenseModal(true); }} data-testid={`finance-expense-edit-${r.recordId}`}>Edit</button>
          )}
          {canVoid && !r.voidedAt && (
            <button className="btn btn-outline btn-sm" style={{ color: "#f2a2a2", borderColor: "#f2a2a2" }} onClick={() => onVoidExpense(r)} data-testid={`finance-expense-void-${r.recordId}`}>Void</button>
          )}
        </div>
      </div>
    );
  };

  // Iter-96 Item 5 — Void Request row renderer used in the Pending
  // Approvals tab. Shows a VOID REQUEST badge, the requester + reason,
  // and Approve/Reject buttons for SA/LA.
  const renderVoidRequestRow = (rec, kind) => {
    const isGiving = kind === "giving";
    const partyLabel = isGiving
      ? (rec.isAnonymous ? "Anonymous" : fullName(memberMap[rec.memberId]))
      : (rec.payeeName || "—");
    const ref = isGiving ? (rec.transactionRef || rec.recordId) : rec.recordId;
    return (
      <div
        key={`voidreq-${kind}-${rec.recordId}`}
        data-testid={`finance-voidreq-row-${rec.recordId}`}
        className="finance-row card-amber"
        style={{
          padding: 12, marginBottom: 8,
          background: "var(--kc-surface)",
          borderRadius: 10,
          border: "1px solid var(--kc-border)",
          borderLeft: "3px solid var(--kc-amber)",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span
                data-testid={`finance-voidreq-badge-${rec.recordId}`}
                style={{
                  padding: "2px 8px",
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                  background: "var(--kc-amber)",
                  color: "#111",
                  borderRadius: 4,
                }}
              >Void Request</span>
              <span style={{ fontWeight: 700 }}>
                {partyLabel} — {fmt(rec.amountCents, currencySymbol)}
              </span>
              <span style={{ fontSize: 11, opacity: 0.7, fontFamily: "monospace" }}>{ref}</span>
            </div>
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
              {isGiving ? "Giving" : `Expense · ${rec.category === "Other" ? (rec.categoryOther || "Other") : rec.category}`}
              {" · "}{fmtDate(rec.date)}
            </div>
            <div style={{ fontSize: 12, marginTop: 6 }}>
              <span style={{ opacity: 0.75 }}>Requested by </span>
              <span style={{ fontWeight: 600 }}>{rec.voidRequestedByName || "—"}</span>
              {rec.voidRequestedAt && (
                <span style={{ opacity: 0.65 }}> · {fmtDate(rec.voidRequestedAt)}</span>
              )}
            </div>
            {rec.voidRequestReason && (
              <div
                style={{ fontSize: 13, marginTop: 6, padding: "8px 10px", background: "rgba(0,0,0,0.25)", borderRadius: 8 }}
                data-testid={`finance-voidreq-reason-${rec.recordId}`}
              >
                <span style={{ opacity: 0.55, fontWeight: 700, marginRight: 6, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6 }}>Reason</span>
                {rec.voidRequestReason}
              </div>
            )}
          </div>
        </div>
        {isAdmin && (
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button
              className="btn"
              style={{ background: "#4ade80", color: "#111", fontWeight: 800, minHeight: 44, padding: "0 22px", fontSize: 14, borderRadius: 10, flex: "1 1 140px" }}
              onClick={() => approveVoidRequest(rec, kind)}
              data-testid={`finance-voidreq-approve-${rec.recordId}`}
            >
              <CustomIcon name="check" size={14} style={{ marginRight: 6 }} /> Approve Void
            </button>
            <button
              className="btn"
              style={{ background: "#f87171", color: "#111", fontWeight: 800, minHeight: 44, padding: "0 22px", fontSize: 14, borderRadius: 10, flex: "1 1 140px" }}
              onClick={() => openRejectVoid(rec, kind)}
              data-testid={`finance-voidreq-reject-${rec.recordId}`}
            >
              <CustomIcon name="warning" size={14} style={{ marginRight: 6 }} /> Reject
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderVoidedRow = (r) => (
    <div key={`${r.__kind}-${r.recordId}`}
         data-testid={`finance-voided-row-${r.recordId}`}
         className="finance-row card-grey"
         style={{
           padding: 12, marginBottom: 8,
           background: "var(--kc-surface)",
           borderRadius: 10,
           border: "1px solid var(--kc-border)",
           borderLeft: "3px solid var(--kc-grey)",
           color: "var(--kc-sub)",
           filter: "grayscale(0.8)",
         }}>
      <div style={{ fontWeight: 600, textDecoration: "line-through" }}>
        {r.__kind === "giving"
          ? `${r.isAnonymous ? "Anonymous" : fullName(memberMap[r.memberId])} — ${fmt(r.amountCents, currencySymbol)}`
          : `${r.payeeName} — ${fmt(r.amountCents, currencySymbol)}`}
      </div>
      <div style={{ fontSize: 12, opacity: 0.8 }}>
        {r.__kind === "giving" ? r.transactionRef : `Expense · ${r.category}`} · Voided {fmtDate(r.voidedAt)}
      </div>
      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>Reason: {r.voidReason}</div>
      {isSuperAdmin && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }} onClick={e => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => setDeleteTarget({ rec: r, kind: r.__kind })}
            data-testid={`finance-voided-delete-${r.recordId}`}
            style={{
              background: "transparent", border: "1px solid var(--kc-red)",
              color: "var(--kc-red)", cursor: "pointer",
              padding: "6px 12px", borderRadius: 8,
              fontFamily: "'Inter',sans-serif", fontSize: 11,
              fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
            }}
          >
            <CustomIcon name="trash" size={11} style={{ marginRight: 4 }} /> Delete Permanently
          </button>
        </div>
      )}
    </div>
  );

  const totalActiveGiving = activeGiving.reduce((s, r) => s + (r.amountCents || 0), 0);
  const totalPendingExpense = pendingExpenses.reduce((s, r) => s + (r.amountCents || 0), 0);

  // ---- Access gate (frontend only — backend also enforces 403) ----
  if (!deptLoaded) {
    return <div style={{ padding: 40, opacity: 0.6, textAlign: "center" }} data-testid="finance-gate-loading">Loading Finance workspace…</div>;
  }
  if (!canAccessFinance) {
    if (isPastorDGA) {
      return <FinancePastorAggregate currencySymbol={currencySymbol} />;
    }
    return <FinanceRestrictedScreen dept={dept} navigate={navigate} />;
  }
  // From here the user has full Finance workspace access.

  return (
    <div className="page finance-page" data-testid="finance-page">
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <CustomIcon name="coin" size={28} />
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0 }}>Finance</h1>
          <div style={{ fontSize: 12, opacity: 0.7 }}>Giving records, expenses, and approvals</div>
        </div>
      </div>

      {/* Summary cards (hidden on Dashboard tab — dashboard has its own richer stats) */}
      {tab !== "dashboard" && (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 20 }}>
        <div style={{ padding: 12, background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", borderRadius: 10, border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)" }} data-testid="finance-total-giving-card">
          <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1 }}>Total Giving</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "var(--kc-accent)" }}>{fmt(totalActiveGiving, currencySymbol)}</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{activeGiving.length} records</div>
        </div>
        <div style={{ padding: 12, background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", borderRadius: 10, border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)" }} data-testid="finance-pending-expense-card">
          <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1 }}>Pending Approvals</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "var(--kc-accent)" }}>{totalPendingApprovalsCount}</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>
            {pendingExpenses.length} expense · {pendingGivingVoidReqs.length + pendingExpenseVoidReqs.length} void req.
          </div>
        </div>
      </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", marginBottom: 16, overflowX: "auto" }}>
        {TABS.map(t => (
          <button
            key={t.key}
            className="btn btn-sm"
            style={{
              background: tab === t.key ? "color-mix(in srgb, var(--kc-accent) 20%, transparent)" : "transparent",
              color: tab === t.key ? "var(--kc-accent)" : "inherit",
              borderRadius: "8px 8px 0 0",
              borderBottom: tab === t.key ? "2px solid var(--kc-accent)" : "2px solid transparent",
              whiteSpace: "nowrap",
            }}
            onClick={() => setTab(t.key)}
            data-testid={`finance-tab-${t.key}`}
          >
            {t.label}
            {t.key === "pending" && totalPendingApprovalsCount > 0 && (
              <span style={{ marginLeft: 6, padding: "2px 6px", background: "var(--kc-accent)", color: "#111", borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
                {totalPendingApprovalsCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {loading ? (
        <div style={{ opacity: 0.6, padding: 20, textAlign: "center" }} data-testid="finance-loading">Loading Finance records…</div>
      ) : (
        <>
          {tab === "dashboard" && (
            <FinanceDashboard
              giving={giving}
              expenses={expenses}
              members={members}
              currencySymbol={currencySymbol}
              onGoPending={() => setTab("pending")}
              onOpenMember={(id) => navHook(`/members/${id}`)}
            />
          )}
          {tab === "giving" && (
            <>
              {renderPeriodSummary({
                period: givingTotalPeriod,
                setPeriod: setGivingTotalPeriod,
                records: filteredActiveGiving,
                testidPrefix: "finance-giving",
                label: "Total Giving",
              })}
              {renderTableToolbar({
                onFilter: () => setGivingFilterOpen(true),
                activeCount: givingChips.length,
                chips: givingChips,
                onExport: () => exportGivingCsv(sortedGiving, memberMap, currencySymbol),
                exportDisabled: sortedGiving.length === 0,
                testidPrefix: "finance-giving",
                extraLeftButtons: (
                  <>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={openBulkGiving}
                      data-testid="finance-giving-bulk-btn"
                      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                      title="Log multiple giving records at once (Sunday offering intake)"
                    >
                      <CustomIcon name="coin" size={12} /> Bulk Entry
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => setImportKind("giving")}
                      data-testid="finance-giving-import-btn"
                      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                      title="Import giving records from a CSV or XLSX file"
                    >
                      <CustomIcon name="download-inbox" size={12} /> Import
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => setShowReceiptModal(true)}
                      data-testid="finance-giving-receipt-btn"
                      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                      title="Generate an official tax receipt for one member"
                    >
                      <CustomIcon name="document" size={12} /> Generate Receipt
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => setShowBatchReceiptModal(true)}
                      data-testid="finance-giving-receipt-batch-btn"
                      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                      title="Generate year-end receipts for every member with giving"
                    >
                      <CustomIcon name="document" size={12} /> Year-End Receipts
                    </button>
                  </>
                ),
              })}
              {sortedGiving.length === 0 ? (
                activeGiving.length === 0
                  ? <div style={{ opacity: 0.6, padding: 20, textAlign: "center" }} data-testid="finance-giving-empty">No giving records yet. Tap the gold + button to log one.</div>
                  : <div style={{ opacity: 0.6, padding: 20, textAlign: "center" }} data-testid="finance-giving-nomatch">No giving records match the current filters.</div>
              ) : renderGrouped({
                records: filteredActiveGiving,
                expandedYears: givingExpandedYears,
                setExpandedYears: setGivingExpandedYears,
                expandedMonths: givingExpandedMonths,
                setExpandedMonths: setGivingExpandedMonths,
                rowRenderer: renderGivingRow,
                testidPrefix: "finance-giving",
              })}
            </>
          )}
          {tab === "expenses" && (
            <>
              {renderPeriodSummary({
                period: expenseTotalPeriod,
                setPeriod: setExpenseTotalPeriod,
                records: filteredActiveExpenses,
                testidPrefix: "finance-expenses",
                label: "Total Expenses",
              })}
              {renderTableToolbar({
                onFilter: () => setExpenseFilterOpen(true),
                activeCount: expenseChips.length,
                chips: expenseChips,
                onExport: () => exportExpensesCsv(filteredActiveExpenses, currencySymbol),
                exportDisabled: filteredActiveExpenses.length === 0,
                testidPrefix: "finance-expenses",
                extraLeftButtons: (
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setImportKind("expense")}
                    data-testid="finance-expenses-import-btn"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                    title="Import expense records from a CSV or XLSX file"
                  >
                    <CustomIcon name="download-inbox" size={12} /> Import
                  </button>
                ),
              })}
              {filteredActiveExpenses.length === 0 ? (
                activeExpenses.length === 0
                  ? <div style={{ opacity: 0.6, padding: 20, textAlign: "center" }} data-testid="finance-expenses-empty">No expense records yet.</div>
                  : <div style={{ opacity: 0.6, padding: 20, textAlign: "center" }} data-testid="finance-expenses-nomatch">No expense records match the current filters.</div>
              ) : renderGrouped({
                records: filteredActiveExpenses,
                expandedYears: expenseExpandedYears,
                setExpandedYears: setExpenseExpandedYears,
                expandedMonths: expenseExpandedMonths,
                setExpandedMonths: setExpenseExpandedMonths,
                rowRenderer: (r) => renderExpenseRow(r, false),
                testidPrefix: "finance-expenses",
              })}
            </>
          )}
          {tab === "pending" && (
            totalPendingApprovalsCount === 0
              ? <div style={{ opacity: 0.6, padding: 20, textAlign: "center" }} data-testid="finance-pending-empty">No pending approvals.</div>
              : (
                <div>
                  {pendingGivingVoidReqs.map(r => renderVoidRequestRow(r, "giving"))}
                  {pendingExpenseVoidReqs.map(r => renderVoidRequestRow(r, "expense"))}
                  {pendingExpenses.map(r => renderExpenseRow(r, isAdmin))}
                </div>
              )
          )}
          {tab === "voided" && (
            voidedRecords.length === 0
              ? <div style={{ opacity: 0.6, padding: 20, textAlign: "center" }} data-testid="finance-voided-empty">No voided records.</div>
              : <div>{voidedRecords.map(renderVoidedRow)}</div>
          )}
          {tab === "anonymous" && (
            <AnonymousSummary
              records={anonymousGiving}
              currencySymbol={currencySymbol}
              isFinanceAdmin={isFinanceAdmin}
              canVoid={canVoid}
              onReassign={onReassign}
              onVoid={onVoidGiving}
              onEdit={(r) => { setEditGiving(r); setShowGivingModal(true); }}
            />
          )}
          {tab === "activity" && (
            <ActivityLogTab
              currencySymbol={currencySymbol}
              canExportCsv={isAdmin}
            />
          )}
        </>
      )}

      {/* Gold + launcher */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setShowLauncher(true)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setShowLauncher(true)}
        style={{
          position: "fixed",
          bottom: 84,
          right: 20,
          width: 56,
          height: 56,
          borderRadius: 28,
          background: "var(--kc-accent)",
          color: "#111",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 30,
          fontWeight: 700,
          boxShadow: "0 6px 16px color-mix(in srgb, var(--kc-accent) 40%, transparent)",
          cursor: "pointer",
          zIndex: 40,
        }}
        data-testid="finance-fab"
        aria-label="Add finance record"
      >+</div>

      {showLauncher && (
        <div className="modal-overlay" onClick={() => setShowLauncher(false)} data-testid="finance-launcher">
          <div className="modal" style={{ maxWidth: 340 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>What are you logging?</h3>
              <button className="modal-close" onClick={() => setShowLauncher(false)}>×</button>
            </div>
            <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button className="btn btn-primary" style={{ padding: "14px", justifyContent: "flex-start" }} onClick={openLogGiving} data-testid="finance-launcher-giving">
                <CustomIcon name="coin" size={18} style={{ marginRight: 10 }} /> Log Giving
              </button>
              <button className="btn btn-primary" style={{ padding: "14px", justifyContent: "flex-start" }} onClick={openBulkGiving} data-testid="finance-launcher-bulk-giving">
                <CustomIcon name="users" size={18} style={{ marginRight: 10 }} /> Bulk Giving Entry
              </button>
              <button className="btn btn-primary" style={{ padding: "14px", justifyContent: "flex-start" }} onClick={openLogExpense} data-testid="finance-launcher-expense">
                <CustomIcon name="expense" size={18} style={{ marginRight: 10 }} /> Log Expense
              </button>
            </div>
          </div>
        </div>
      )}

      {showGivingModal && (
        <GivingEntryModal
          open
          members={members}
          editRecord={editGiving}
          currencySymbol={currencySymbol}
          onClose={() => { setShowGivingModal(false); setEditGiving(null); }}
          onSaved={() => reload()}
        />
      )}
      {showBulkGivingModal && (
        <BulkEntryModal
          open
          members={members}
          currencySymbol={currencySymbol}
          onClose={() => setShowBulkGivingModal(false)}
          onSaved={() => reload()}
        />
      )}
      {importKind && (
        <FinanceImportModal
          open
          kind={importKind}
          members={members}
          currencySymbol={currencySymbol}
          onClose={() => setImportKind(null)}
          onSaved={() => reload()}
        />
      )}
      {showReceiptModal && (
        <ReceiptGenerateModal
          open
          members={members}
          currencySymbol={currencySymbol}
          appSettings={appSettings}
          onClose={() => setShowReceiptModal(false)}
          onGenerated={() => reload()}
        />
      )}
      {showBatchReceiptModal && (
        <ReceiptBatchModal
          open
          currencySymbol={currencySymbol}
          appSettings={appSettings}
          onClose={() => setShowBatchReceiptModal(false)}
          onGenerated={() => reload()}
        />
      )}
      {lightboxSrc && (
        <ReceiptLightbox
          src={lightboxSrc}
          filename={lightboxFilename}
          onClose={() => setLightboxSrc(null)}
        />
      )}
      {showExpenseModal && (
        <ExpenseEntryModal
          open
          members={members}
          editRecord={editExpense}
          currencySymbol={currencySymbol}
          thresholds={thresholds}
          onClose={() => { setShowExpenseModal(false); setEditExpense(null); }}
          onSaved={() => reload()}
        />
      )}

      <FinanceFilterPanel
        open={givingFilterOpen}
        onClose={() => setGivingFilterOpen(false)}
        title="Filter Giving Records"
        filters={givingFilters}
        onChange={setGivingFilters}
        onClear={() => setGivingFilters(EMPTY_GIVING_FILTERS)}
        schema={[
          { key: "dateRange",      type: "dateRange",   label: "Date Range" },
          { key: "memberQuery",    type: "text",        label: "Member Name", placeholder: "Search by name…" },
          { key: "categories",     type: "multiSelect", label: "Categories", options: GIVING_CATEGORIES },
          { key: "paymentMethods", type: "multiSelect", label: "Payment Method", options: PAYMENT_METHODS },
          { key: "anonymousOnly",  type: "checkbox",    label: "Anonymous", checkboxLabel: "Show only anonymous giving" },
          { key: "amountRange",    type: "amountRange", label: "Amount Range", currencySymbol },
        ]}
        testidPrefix="finance-giving-filter"
      />

      <FinanceFilterPanel
        open={expenseFilterOpen}
        onClose={() => setExpenseFilterOpen(false)}
        title="Filter Expenses"
        filters={expenseFilters}
        onChange={setExpenseFilters}
        onClear={() => setExpenseFilters(EMPTY_EXPENSE_FILTERS)}
        schema={[
          { key: "dateRange",   type: "dateRange",   label: "Date Range" },
          { key: "payeeQuery",  type: "text",        label: "Payee Name", placeholder: "Search by payee…" },
          { key: "categories",  type: "multiSelect", label: "Categories", options: EXPENSE_CATEGORIES },
          { key: "statuses",    type: "multiSelect", label: "Approval Status", options: EXPENSE_STATUSES },
          { key: "amountRange", type: "amountRange", label: "Amount Range", currencySymbol },
        ]}
        testidPrefix="finance-expenses-filter"
      />

      {deleteTarget && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteTarget(null)} data-testid="finance-permanent-delete-modal">
          <div className="modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0, color: "var(--kc-red)" }}>Delete Permanently?</h3>
              <button className="modal-close" onClick={() => setDeleteTarget(null)}>×</button>
            </div>
            <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ padding: 12, background: "color-mix(in srgb, var(--kc-red) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-red) 25%, transparent)", borderRadius: 8, fontSize: 13 }}>
                <div style={{ fontWeight: 700, color: "var(--kc-text)", marginBottom: 4 }}>
                  {deleteTarget.kind === "giving"
                    ? `${deleteTarget.rec.isAnonymous ? "Anonymous" : fullName(memberMap[deleteTarget.rec.memberId])} — ${fmt(deleteTarget.rec.amountCents, currencySymbol)}`
                    : `${deleteTarget.rec.payeeName} — ${fmt(deleteTarget.rec.amountCents, currencySymbol)}`}
                </div>
                <div style={{ fontSize: 12, color: "var(--kc-sub)" }}>
                  {deleteTarget.kind === "giving"
                    ? `${deleteTarget.rec.transactionRef || "—"} · ${fmtDate(deleteTarget.rec.date)}`
                    : `${deleteTarget.rec.category} · ${fmtDate(deleteTarget.rec.date)}`}
                </div>
                <div style={{ fontSize: 12, color: "var(--kc-sub)", marginTop: 4 }}>
                  Voided {fmtDate(deleteTarget.rec.voidedAt)} · Reason: {deleteTarget.rec.voidReason || "—"}
                </div>
              </div>
              <div style={{ color: "var(--kc-text)", fontSize: 13, lineHeight: 1.5 }}>
                This action is permanent and cannot be undone. Tap <b>Delete Permanently</b> to permanently remove this record.
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setDeleteTarget(null)}
                  data-testid="finance-permanent-delete-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={commitPermanentDelete}
                  data-testid="finance-permanent-delete-confirm"
                  style={{
                    background: "var(--kc-red)", color: "#fff",
                    border: "none", borderRadius: 8,
                    padding: "10px 18px", fontWeight: 700,
                    fontFamily: "'Inter',sans-serif",
                    letterSpacing: "0.04em", textTransform: "uppercase",
                    fontSize: 12, cursor: "pointer",
                  }}
                >
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {reassignRec && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setReassignRec(null)} data-testid="finance-reassign-modal">
          <div className="modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>Reassign to Member</h3>
              <button className="modal-close" onClick={() => setReassignRec(null)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 10 }}>
                Link anonymous giving <b>{reassignRec.transactionRef}</b> ({fmt(reassignRec.amountCents, currencySymbol)}) to a named member.
              </div>
              <input
                type="text"
                className="form-input"
                placeholder="Search by name…"
                value={reassignSearch}
                onChange={(e) => setReassignSearch(e.target.value)}
                data-testid="finance-reassign-search"
              />
              <div style={{ maxHeight: 260, overflowY: "auto", marginTop: 6, border: "1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent)", borderRadius: 8 }}>
                {(members || [])
                  .filter(m => `${m.firstName || ""} ${m.lastName || ""}`.toLowerCase().includes(reassignSearch.trim().toLowerCase()))
                  .slice(0, 25)
                  .map(m => (
                    <div
                      key={m.id}
                      onClick={() => commitReassign(m.id)}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: 10, cursor: "pointer", borderBottom: "1px solid color-mix(in srgb, var(--kc-accent) 10%, transparent)" }}
                      data-testid={`finance-reassign-result-${m.id}`}
                    >
                      <Avatar photo={m.photo} name={fullName(m)} memberId={m.id} status={m.memberStatus} size={30} />
                      <span>{fullName(m)}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Iter-96 Item 5 — Void Request modals */}
      <VoidRequestModal
        open={Boolean(voidReqTarget)}
        title={
          voidReqTarget?.kind === "giving"
            ? `Request Void — ${voidReqTarget?.rec?.transactionRef || ""}`
            : `Request Void — ${voidReqTarget?.rec?.payeeName || ""}`
        }
        helpText={
          isAdmin
            ? "This void will be applied immediately and logged in the Activity Log."
            : "Your void request will be sent to a Super or Limited Admin for approval. The record stays active until an admin approves it."
        }
        submitLabel={isAdmin ? "Void Now" : "Submit Request"}
        danger={isAdmin}
        onSubmit={submitVoidRequest}
        onClose={() => setVoidReqTarget(null)}
        testidPrefix="finance-void-request"
      />
      <VoidRequestModal
        open={Boolean(voidRejectTarget)}
        title="Reject Void Request"
        helpText="Explain why this void request should be rejected. The requester will receive an in-app notification with your reason."
        submitLabel="Reject Void Request"
        danger
        onSubmit={submitVoidRejection}
        onClose={() => setVoidRejectTarget(null)}
        testidPrefix="finance-void-reject"
      />
    </div>
  );
}

export default FinancePage;

// ============================================================
// Filter matchers, chip builders and CSV export helpers.
// Kept at module scope so they can be reused / tested cleanly
// without React re-render churn.
// ============================================================

function inDateRange(iso, range) {
  if (!range || (!range.from && !range.to)) return true;
  if (!iso) return false;
  const d = iso.length === 10 ? iso : iso.slice(0, 10);
  if (range.from && d < range.from) return false;
  if (range.to && d > range.to) return false;
  return true;
}

function inAmountRange(cents, range) {
  if (!range || (range.min === "" && range.max === "") || (range.min == null && range.max == null)) return true;
  const dollars = (Number(cents) || 0) / 100;
  if (range.min !== "" && range.min != null && dollars < Number(range.min)) return false;
  if (range.max !== "" && range.max != null && dollars > Number(range.max)) return false;
  return true;
}

function matchGivingFilters(r, f, memberMap) {
  if (!inDateRange(r.date, f.dateRange)) return false;
  if (!inAmountRange(r.amountCents, f.amountRange)) return false;
  if (Array.isArray(f.categories) && f.categories.length > 0 && !f.categories.includes(r.category)) return false;
  if (Array.isArray(f.paymentMethods) && f.paymentMethods.length > 0 && !f.paymentMethods.includes(r.paymentMethod)) return false;
  if (f.anonymousOnly && !r.isAnonymous) return false;
  if (f.memberQuery && f.memberQuery.trim() !== "") {
    if (r.isAnonymous) return false;
    const m = memberMap[r.memberId];
    const name = `${m?.firstName || ""} ${m?.lastName || ""}`.toLowerCase();
    if (!name.includes(f.memberQuery.trim().toLowerCase())) return false;
  }
  return true;
}

function matchExpenseFilters(r, f) {
  if (!inDateRange(r.date, f.dateRange)) return false;
  if (!inAmountRange(r.amountCents, f.amountRange)) return false;
  if (Array.isArray(f.categories) && f.categories.length > 0 && !f.categories.includes(r.category)) return false;
  if (Array.isArray(f.statuses) && f.statuses.length > 0 && !f.statuses.includes(r.approvalStatus)) return false;
  if (f.payeeQuery && f.payeeQuery.trim() !== "") {
    const q = f.payeeQuery.trim().toLowerCase();
    if (!(r.payeeName || "").toLowerCase().includes(q)) return false;
  }
  return true;
}

// -------- Chip builders --------
// Each returns [{ id, label, onRemove }] describing the active filters.
function buildGivingChips(f, setF, sym) {
  const chips = [];
  if (f.dateRange?.from || f.dateRange?.to) {
    chips.push({
      id: "date",
      label: `Date: ${f.dateRange.from || "…"} → ${f.dateRange.to || "…"}`,
      onRemove: () => setF({ ...f, dateRange: { from: "", to: "" } }),
    });
  }
  if (f.memberQuery?.trim()) {
    chips.push({ id: "member", label: `Member: ${f.memberQuery.trim()}`, onRemove: () => setF({ ...f, memberQuery: "" }) });
  }
  (f.categories || []).forEach(c => {
    chips.push({
      id: `cat-${c}`,
      label: `Category: ${c}`,
      onRemove: () => setF({ ...f, categories: f.categories.filter(x => x !== c) }),
    });
  });
  (f.paymentMethods || []).forEach(p => {
    chips.push({
      id: `pay-${p}`,
      label: `Method: ${p}`,
      onRemove: () => setF({ ...f, paymentMethods: f.paymentMethods.filter(x => x !== p) }),
    });
  });
  if (f.anonymousOnly) {
    chips.push({ id: "anon", label: "Anonymous only", onRemove: () => setF({ ...f, anonymousOnly: false }) });
  }
  if (f.amountRange?.min !== "" || f.amountRange?.max !== "") {
    const min = f.amountRange.min !== "" && f.amountRange.min != null ? `${sym}${f.amountRange.min}` : "…";
    const max = f.amountRange.max !== "" && f.amountRange.max != null ? `${sym}${f.amountRange.max}` : "…";
    chips.push({
      id: "amount",
      label: `Amount: ${min} → ${max}`,
      onRemove: () => setF({ ...f, amountRange: { min: "", max: "" } }),
    });
  }
  return chips;
}

function buildExpenseChips(f, setF, sym) {
  const chips = [];
  if (f.dateRange?.from || f.dateRange?.to) {
    chips.push({
      id: "date",
      label: `Date: ${f.dateRange.from || "…"} → ${f.dateRange.to || "…"}`,
      onRemove: () => setF({ ...f, dateRange: { from: "", to: "" } }),
    });
  }
  if (f.payeeQuery?.trim()) {
    chips.push({ id: "payee", label: `Payee: ${f.payeeQuery.trim()}`, onRemove: () => setF({ ...f, payeeQuery: "" }) });
  }
  (f.categories || []).forEach(c => {
    chips.push({
      id: `cat-${c}`,
      label: `Category: ${c}`,
      onRemove: () => setF({ ...f, categories: f.categories.filter(x => x !== c) }),
    });
  });
  (f.statuses || []).forEach(s => {
    chips.push({
      id: `status-${s}`,
      label: `Status: ${s}`,
      onRemove: () => setF({ ...f, statuses: f.statuses.filter(x => x !== s) }),
    });
  });
  if (f.amountRange?.min !== "" || f.amountRange?.max !== "") {
    const min = f.amountRange.min !== "" && f.amountRange.min != null ? `${sym}${f.amountRange.min}` : "…";
    const max = f.amountRange.max !== "" && f.amountRange.max != null ? `${sym}${f.amountRange.max}` : "…";
    chips.push({
      id: "amount",
      label: `Amount: ${min} → ${max}`,
      onRemove: () => setF({ ...f, amountRange: { min: "", max: "" } }),
    });
  }
  return chips;
}

// -------- CSV export --------
function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename, rows) {
  const text = rows.map(r => r.map(csvEscape).join(",")).join("\r\n");
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportGivingCsv(records, memberMap, sym) {
  const header = ["Date", "Member", "Category", "Amount", "Currency", "Payment Method", "Ref", "Anonymous"];
  const rows = [header];
  records.forEach(r => {
    rows.push([
      r.date || "",
      r.isAnonymous ? "Anonymous" : `${memberMap[r.memberId]?.firstName || ""} ${memberMap[r.memberId]?.lastName || ""}`.trim(),
      r.category === "Other" ? (r.categoryOther || "Other") : (r.category || ""),
      ((Number(r.amountCents) || 0) / 100).toFixed(2),
      sym,
      r.paymentMethod || "",
      r.transactionRef || "",
      r.isAnonymous ? "Yes" : "No",
    ]);
  });
  downloadCsv(`giving-records-${todayStamp()}.csv`, rows);
}

function exportExpensesCsv(records, sym) {
  const header = ["Date", "Payee", "Category", "Amount", "Currency", "Status", "Ref", "Approved By", "Rejected Reason"];
  const rows = [header];
  records.forEach(r => {
    rows.push([
      r.date || "",
      r.payeeName || "",
      r.category === "Other" ? (r.categoryOther || "Other") : (r.category || ""),
      ((Number(r.amountCents) || 0) / 100).toFixed(2),
      sym,
      r.approvalStatus || "",
      r.transactionRef || "",
      r.approvedBy || "",
      r.rejectedReason || "",
    ]);
  });
  downloadCsv(`expenses-${todayStamp()}.csv`, rows);
}

function todayStamp() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
}

// ============================================================
// Presentational helpers used by the sortable Giving table and
// the Anonymous Summary tab.
// ============================================================
const GIV_TH = { textAlign: "left", padding: "10px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 };
const GIV_TD = { padding: "10px 10px", whiteSpace: "nowrap", verticalAlign: "middle" };

function SortableTh({ label, onClick, indicator, align = "left", testid }) {
  return (
    <th
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick && onClick()}
      data-testid={testid}
      style={{ ...GIV_TH, textAlign: align, cursor: "pointer", userSelect: "none" }}
    >
      {label}{indicator}
    </th>
  );
}

// ------- Anonymous Summary Tab -------
// Lists all anonymous giving records, plus a total, plus a
// per-row Reassign action so Finance admins can convert an
// anonymous record into a named contribution.
function AnonymousSummary({ records, currencySymbol, isFinanceAdmin, canVoid, onReassign, onVoid, onEdit }) {
  const total = records.reduce((s, r) => s + (Number(r.amountCents) || 0), 0);
  const totalStr = `${currencySymbol}${(total / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (records.length === 0) {
    return (
      <div style={{ opacity: 0.6, padding: 20, textAlign: "center" }} data-testid="finance-anonymous-empty">
        No anonymous giving records.
      </div>
    );
  }

  return (
    <div data-testid="finance-anonymous-tab">
      <div
        style={{
          padding: 16, marginBottom: 14,
          background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)",
          border: "1px solid color-mix(in srgb, var(--kc-accent) 28%, transparent)",
          borderRadius: 12,
          display: "flex", alignItems: "center", gap: 12,
        }}
        data-testid="finance-anonymous-summary-card"
      >
        <CustomIcon name="coin" size={26} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 16, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Anonymous Giving
          </div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            {records.length} {records.length === 1 ? "record" : "records"} — reassign to a member when the giver is identified.
          </div>
        </div>
        <div
          style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-accent)" }}
          data-testid="finance-anonymous-total"
        >
          {totalStr}
        </div>
      </div>

      <div style={{ overflowX: "auto", border: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)", borderRadius: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="finance-anonymous-table">
          <thead>
            <tr style={{ background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", color: "var(--kc-accent)" }}>
              <th style={GIV_TH}>Date</th>
              <th style={GIV_TH}>Category</th>
              <th style={{ ...GIV_TH, textAlign: "right" }}>Amount</th>
              <th style={GIV_TH}>Ref</th>
              <th style={{ ...GIV_TH, textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {records.map(r => (
              <tr key={r.recordId} style={{ borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 10%, transparent)" }} data-testid={`finance-anonymous-row-${r.recordId}`}>
                <td style={GIV_TD}>{fmtDate(r.date)}</td>
                <td style={GIV_TD}>{r.category === "Other" ? (r.categoryOther || "Other") : r.category}</td>
                <td style={{ ...GIV_TD, textAlign: "right", color: "var(--kc-accent)", fontWeight: 700, fontFamily: "monospace" }}>
                  {`${currencySymbol}${((Number(r.amountCents) || 0) / 100).toFixed(2)}`}
                </td>
                <td style={{ ...GIV_TD, fontFamily: "monospace", fontSize: 12, opacity: 0.85 }}>{r.transactionRef}</td>
                <td style={{ ...GIV_TD, textAlign: "right" }}>
                  <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {isFinanceAdmin && (
                      <button
                        className="btn btn-sm"
                        style={{ background: "var(--kc-accent)", color: "#111", fontWeight: 700, minHeight: 40, padding: "0 14px" }}
                        onClick={() => onReassign(r)}
                        data-testid={`finance-anonymous-reassign-${r.recordId}`}
                      >
                        Reassign
                      </button>
                    )}
                    <button className="btn btn-outline btn-sm" onClick={() => onEdit(r)} data-testid={`finance-anonymous-edit-${r.recordId}`}>Edit</button>
                    {canVoid && (
                      <button
                        className="btn btn-outline btn-sm"
                        style={{ color: "#f2a2a2", borderColor: "#f2a2a2" }}
                        onClick={() => onVoid(r)}
                        data-testid={`finance-anonymous-void-${r.recordId}`}
                      >
                        Void
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
